import { useMutation, useQuery } from '@apollo/client';
import type { ErrorResponse } from '@apollo/client/link/error';
import React, { useEffect, useRef, useState } from 'react';
import { SessionActions } from '../../account/sessionSlice';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import LoadingIndicator from '../../components/LoadingIndicator';
import {
  ACTION_PLAN_SUBSCRIBE,
  CATEGORY_ADMIN,
  LAYER,
} from '../../constants/analytics';
import type {
  PlanInterval,
  PlanTier,
  StartTrialTierInput,
} from '../../graphql/generated/graphql';
import SettingsContainerView from '../../settings/SettingsContainerView';
import UIActions from '../../ui/UIActions';
import { track } from '../../utils/analytics';
import handleApolloError from '../../utils/error/handleApolloError';
import {
  useLocation,
  useRouter,
  withRouter,
} from '../../utils/router/v3Bridge';
import styles from './AdminBillingView.module.scss';
import BillingCreateView from './BillingCreateView';
import BillingStatusView from './BillingStatusView';
import ChurnModal from './ChurnModal';
import { INTERVAL_KEY, TIER_KEY } from './billingQuery';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';

const G2_REFERRAL_CODE = 'g2';
const G2_PIXEL_URL =
  process.env.NODE_ENV === 'production'
    ? 'https://tracking.g2crowd.com/funnels/4db85917-76b8-4b40-b1df-a4454f6ae76e.gif?stage=finish&stype=offer'
    : 'https://api.slabdev.com/track/g2-test';

const ADMIN_BILLING_VIEW_BILLING_SUMMARY_FRAGMENT = gql(`
  fragment AdminBillingView_billingSummary on BillingSummary {
    id
    status
    plan {
      id
      tier
    }
    ...BillingCreateView_billingSummary
    ...BillingStatusView_billingSummary
  }
`);

const ADMIN_BILLING_VIEW_BILLING_PRICING_FRAGMENT = gql(`
  fragment AdminBillingView_billingPricing on BillingPricing {
    ...BillingCreateView_billingPricing
    ...BillingStatusView_billingPricing
  }
`);

// eslint-disable-next-line slab-graphql/no-unused-colocated-fragments
const GET_BILLING_AND_PLANS = gql(`
  query getBillingAndPlans {
    session: currentSession {
      organization {
        id
        referralCode
        ...OrganizationBilling
      }
    }
    billingSummary {
      id
      userCount
      pendingActionUrl
      ...AdminBillingView_billingSummary
    }
    billingPricing {
      ...AdminBillingView_billingPricing
    }
  }
`);

const START_TRIAL = gql(`
  mutation startTrial($planTier: StartTrialTierInput!) {
    billingSummary: startTrial(planTier: $planTier) {
      ...AdminBillingView_billingSummary
    }
  }
`);

const UPDATE_BILLING = gql(`
  mutation updateBilling(
    $email: String
    $source: String
    $coupon: String
    $planTier: PlanTier
    $planInterval: PlanInterval
  ) {
    billingSummary: updateBilling(
      email: $email
      source: $source
      coupon: $coupon
      planTier: $planTier
      planInterval: $planInterval
    ) {
      ...AdminBillingView_billingSummary
    }
  }
`);

const CANCEL_BILLING = gql(`
  mutation cancelBilling($reason: String, $details: String) {
    billingSummary: cancelBilling(reason: $reason, details: $details) {
      ...AdminBillingView_billingSummary
    }
  }
`);

type CreateBillingVariables = {
  source: string;
  coupon?: string;
  email?: string;
  planTier: PlanTier;
  planInterval: PlanInterval;
};

type UpdateBillingVariables = {
  email?: string;
  source?: string;
  planTier?: PlanTier;
  planInterval?: PlanInterval;
};

interface AdminBillingViewProps {
  billingPricing?: DocumentType<
    typeof ADMIN_BILLING_VIEW_BILLING_PRICING_FRAGMENT
  >;
  billingSummary?: DocumentType<
    typeof ADMIN_BILLING_VIEW_BILLING_SUMMARY_FRAGMENT
  >;
  isTrialEnded: boolean;
  updatingBilling: boolean;
  shouldRenderG2Pixel: boolean;
  onStartTrial: (planTier: StartTrialTierInput) => void;
  onCreateBilling: (params: CreateBillingVariables) => void;
  onUpdateBilling: (values: UpdateBillingVariables) => Promise<unknown>;
  onCancelBilling: () => void;
}

export const BaseAdminBillingView = ({
  billingPricing,
  billingSummary,
  isTrialEnded,
  updatingBilling,
  shouldRenderG2Pixel,
  onStartTrial,
  onCreateBilling,
  onUpdateBilling,
  onCancelBilling,
}: AdminBillingViewProps) => {
  const { query } = useLocation();
  const router = useRouter();

  const initialTierRef = useRef(query[TIER_KEY] as PlanTier | null);
  const initialIntervalRef = useRef(query[INTERVAL_KEY] as PlanInterval | null);

  const loading = !(billingSummary && billingPricing);

  useEffect(() => {
    if (!loading && (query[TIER_KEY] || query[INTERVAL_KEY])) {
      router.push('/admin/billing');
    }
  }, [loading, query, router]);

  return (
    <SettingsContainerView
      title="Billing"
      subtitle="Payments and subscriptions for your team."
      className="billing-tab"
    >
      {loading ? (
        <LoadingIndicator />
      ) : (
        <div className={styles.contentWrapper}>
          {isTrialEnded ||
          (billingSummary.plan && billingSummary.plan.tier === 'FREE') ||
          ['TRIALING', 'CANCELED', 'CANCELED_AND_ACTIVE'].includes(
            billingSummary.status,
          ) ? (
            <BillingCreateView
              billingSummary={billingSummary}
              billingPricing={billingPricing}
              startTrial={onStartTrial}
              createBilling={onCreateBilling}
              updatingBilling={updatingBilling}
              isTrialEnded={isTrialEnded}
              initialTier={initialTierRef.current}
              initialInterval={initialIntervalRef.current}
            />
          ) : (
            <BillingStatusView
              billingSummary={billingSummary}
              billingPricing={billingPricing}
              updateBilling={onUpdateBilling}
              cancelBilling={onCancelBilling}
              initialTier={initialTierRef.current}
              initialInterval={initialIntervalRef.current}
            />
          )}
        </div>
      )}
      {shouldRenderG2Pixel && <img alt="" src={G2_PIXEL_URL} />}
    </SettingsContainerView>
  );
};

const AdminBillingView = () => {
  const { query, pathname } = useLocation();
  const router = useRouter();
  const dispatch = useAppDispatch();

  const { data: { billingSummary, billingPricing, session } = {}, refetch } =
    useQuery(GET_BILLING_AND_PLANS, { fetchPolicy: 'network-only' });

  // billingSummary will always have the latest status, while session.organization may be stale
  const billingStatus = billingSummary?.status;

  useEffect(() => {
    if (billingStatus != null && session != null) {
      dispatch(
        SessionActions.setCurrentOrganization({
          ...session.organization,
          billingStatus,
        }),
      );
    }
  }, [billingStatus, dispatch, session]);

  const [startTrial] = useMutation(START_TRIAL);
  const [updateBilling] = useMutation(UPDATE_BILLING);
  const [cancelBilling] = useMutation(CANCEL_BILLING);

  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const [updatingBilling, setUpdatingBilling] = useState(false);
  const [shouldRenderG2Pixel, setShouldRenderG2Pixel] = useState(false);

  useEffect(() => {
    if (query.error) {
      dispatch(UIActions.pushToast({ level: 'error', message: query.error }));
      router.replace(pathname);
    }

    if (query.success) {
      dispatch(
        UIActions.pushToast({ level: 'success', message: query.success }),
      );
      router.replace(pathname);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpdateSuccess = () =>
    refetch().then(({ data }) => {
      if (data) {
        dispatch(
          SessionActions.setCurrentOrganization(data.session.organization),
        );
      }

      if (data.billingSummary.pendingActionUrl) {
        window.location.href = data.billingSummary.pendingActionUrl;
      } else {
        dispatch(
          UIActions.pushToast({
            level: 'success',
            message: 'Updated your subscription!',
          }),
        );
        // Get new __slab.FEATURES
        setTimeout(() => {
          window.location.reload();
        }, 2500);
      }
    });

  const handleUpdateErrors = (error: ErrorResponse) => {
    const showError = (message: string) => {
      dispatch(UIActions.pushToast({ level: 'error', message }, 30000));
    };

    handleApolloError(error, {
      CARD_DECLINED: () => {
        showError('Your card has been declined. Please use a different card.');
      },
      UNKNOWN: () => {
        showError(
          'There was an error updating your subscription. Please refresh the page.',
        );
      },
    });
  };

  const handleStartTrial = (planTier: StartTrialTierInput) => {
    return startTrial({ variables: { planTier } })
      .then(handleUpdateSuccess)
      .catch(handleUpdateErrors);
  };

  const handleUpdateBilling = (params: UpdateBillingVariables) => {
    setUpdatingBilling(true);
    dispatch(UIActions.clearToasts());

    return updateBilling({ variables: params })
      .then(handleUpdateSuccess)
      .catch(handleUpdateErrors)
      .finally(() => setUpdatingBilling(false));
  };

  const handleCreateBilling = (params: CreateBillingVariables) => {
    if (!session || !billingSummary) {
      return undefined;
    }
    const { referralCode } = session.organization;
    return handleUpdateBilling(params).then(() => {
      track(ACTION_PLAN_SUBSCRIBE, {
        label: `${params.planTier}_${params.planInterval}`.toLowerCase(),
        value: billingSummary.userCount,
        referral_code: referralCode,
        category: CATEGORY_ADMIN,
        layer: LAYER,
      });

      setShouldRenderG2Pixel(referralCode === G2_REFERRAL_CODE);
    });
  };

  const handleCancelBilling = () => {
    dispatch(
      UIActions.openModal(ChurnModal, {
        disableOutsideClose: true,
        onSubmit: (variables: {
          reason?: string | null;
          details?: string | null;
        }) =>
          cancelBilling({ variables })
            .then(handleUpdateSuccess)
            .catch(handleUpdateErrors),
      }),
    );
  };

  return (
    <BaseAdminBillingView
      billingPricing={billingPricing}
      billingSummary={billingSummary}
      isTrialEnded={!!session?.organization.isTrialEnded}
      updatingBilling={updatingBilling}
      shouldRenderG2Pixel={shouldRenderG2Pixel}
      onStartTrial={handleStartTrial}
      onCancelBilling={handleCancelBilling}
      onCreateBilling={handleCreateBilling}
      onUpdateBilling={handleUpdateBilling}
    />
  );
};

export default withRouter(AdminBillingView);
